import { useState, useEffect, useCallback } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";
import {
  Search,
  DollarSign,
  Users,
  Building2,
  FileText,
  Clock,
  CheckCircle,
  AlertCircle,
  Mail,
  Plus,
  RefreshCw,
  Loader2,
  Calendar,
  Phone,
  ExternalLink,
  ChevronRight,
  TrendingUp,
  BarChart3,
  Target,
  Send,
  Star,
  Briefcase,
  Receipt,
  Lightbulb,
} from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { ScrollArea } from "../components/ui/scroll-area";
import { Separator } from "../components/ui/separator";
import { API_URL, API_BASE_URL } from "../config/api";


export default function OutlookAddinSalesPage() {
  // Office.js loading state
  const [officeLoaded, setOfficeLoaded] = useState(false);
  const [isOutlook, setIsOutlook] = useState(false);
  
  // Auth state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authToken, setAuthToken] = useState(localStorage.getItem("outlook_sales_token"));
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);
  
  // 2FA state
  const [requires2FA, setRequires2FA] = useState(false);
  const [twoFactorCode, setTwoFactorCode] = useState("");
  const [twoFactorEmail, setTwoFactorEmail] = useState("");

  // Email context
  const [currentEmail, setCurrentEmail] = useState(null);
  const [emailBody, setEmailBody] = useState("");

  // Active tab
  const [activeTab, setActiveTab] = useState("customer");

  // Suggestion Box
  const [suggestionText, setSuggestionText] = useState("");
  const [submittingSuggestion, setSubmittingSuggestion] = useState(false);

  // Customer/Contact data
  const [customerData, setCustomerData] = useState(null);
  const [customerLoading, setCustomerLoading] = useState(false);

  // Quotes
  const [quotes, setQuotes] = useState([]);
  const [quotesLoading, setQuotesLoading] = useState(false);
  const [showNewQuoteDialog, setShowNewQuoteDialog] = useState(false);
  const [parsingEmail, setParsingEmail] = useState(false);
  const [parseConfidence, setParseConfidence] = useState(null);
  const [parseSource, setParseSource] = useState(null);
  // Field shape mirrors europacific.com/en/quote (Service → Route → Cargo → Contact)
  const emptyQuote = {
    service_type: "fcl",  // fcl | lcl | air | inland | multimodal
    origin_country: "", origin_city: "", origin_address: "", origin_port_airport: "",
    destination_country: "", destination_city: "", destination_address: "", destination_port_airport: "",
    container_type: "", container_quantity: 1,
    total_weight_kg: "", total_volume_cbm: "",
    commodity: "",
    cargo_ready_date: "",
    incoterm: "",
    special_requirements: "",
    company_name: "", contact_name: "", email: "", phone: "",
    temperature_controlled: false, temperature_celsius: "",
    hazardous: false,
  };
  const [newQuote, setNewQuote] = useState(emptyQuote);

  // Offers
  const [offers, setOffers] = useState([]);
  const [offersLoading, setOffersLoading] = useState(false);

  // Pipeline / Stats
  const [pipelineStats, setPipelineStats] = useState(null);

  // Load Office.js dynamically
  useEffect(() => {
    const loadOfficeJS = async () => {
      if (typeof window.Office !== "undefined") {
        setOfficeLoaded(true);
        return;
      }

      const script = document.createElement("script");
      script.src = "https://appsforoffice.microsoft.com/lib/1/hosted/office.js";
      script.async = true;
      
      script.onload = () => {
        if (typeof window.Office !== "undefined") {
          window.Office.onReady((info) => {
            setOfficeLoaded(true);
            if (info.host === window.Office.HostType.Outlook) {
              setIsOutlook(true);
            }
          });
        }
      };
      
      document.head.appendChild(script);
    };

    loadOfficeJS();
  }, []);

  // Check auth on mount
  useEffect(() => {
    if (authToken) {
      setIsAuthenticated(true);
      if (officeLoaded && isOutlook) {
        getEmailContext();
      }
    }
  }, [authToken, officeLoaded, isOutlook]);

  // Submit suggestion
  const submitSuggestion = async () => {
    if (!suggestionText.trim()) {
      toast.error("Please enter your suggestion");
      return;
    }

    setSubmittingSuggestion(true);
    try {
      await axios.post(`${API_URL}/outlook/suggestions`, {
        suggestion: suggestionText,
        plugin: "sales",
        user_email: currentEmail?.sender_email,
        email_context: currentEmail?.subject,
      }, {
        headers: { Authorization: `Bearer ${authToken}` }
      });

      toast.success("Thank you for your suggestion!");
      setSuggestionText("");
    } catch (error) {
      toast.error("Failed to submit suggestion");
    } finally {
      setSubmittingSuggestion(false);
    }
  };

  // Get email context from Outlook
  const getEmailContext = useCallback(() => {
    if (!officeLoaded || !isOutlook) return;

    try {
      const item = window.Office.context.mailbox.item;
      if (item) {
        const emailData = {
          subject: item.subject,
          from: item.from?.emailAddress || item.sender?.emailAddress,
          fromName: item.from?.displayName || item.sender?.displayName,
          to: item.to?.map(r => r.emailAddress) || [],
          cc: item.cc?.map(r => r.emailAddress) || [],
          dateReceived: item.dateTimeCreated,
          conversationId: item.conversationId,
        };
        setCurrentEmail(emailData);

        // Get body
        item.body.getAsync(window.Office.CoercionType.Text, (result) => {
          if (result.status === window.Office.AsyncResultStatus.Succeeded) {
            setEmailBody(result.value);
          }
        });

        // Auto-lookup customer
        if (emailData.from) {
          lookupCustomer(emailData.from);
        }
      }
    } catch (error) {
      console.error("Failed to get email context:", error);
    }
  }, [officeLoaded, isOutlook]);

  // Login handler
  const handleLogin = async () => {
    if (!email || !password) {
      toast.error("Please enter email and password");
      return;
    }

    setLoginLoading(true);
    try {
      const response = await axios.post(`${API_URL}/auth/login`, {
        email,
        password
      });
      
      // Check if 2FA is required
      if (response.data.requires_2fa) {
        setRequires2FA(true);
        setTwoFactorEmail(response.data.email || email);
        toast.info("Verification code sent to your email");
        setLoginLoading(false);
        return;
      }
      
      const token = response.data.access_token;
      localStorage.setItem("outlook_sales_token", token);
      setAuthToken(token);
      setIsAuthenticated(true);
      toast.success("Logged in successfully!");
      
      if (officeLoaded && isOutlook) {
        getEmailContext();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Login failed");
    } finally {
      setLoginLoading(false);
    }
  };

  // Handle 2FA verification
  const handleVerify2FA = async () => {
    setLoginLoading(true);
    try {
      const response = await axios.post(`${API_URL}/auth/verify-2fa`, {
        email: twoFactorEmail,
        code: twoFactorCode,
      });
      
      const token = response.data.access_token;
      localStorage.setItem("outlook_sales_token", token);
      setAuthToken(token);
      setIsAuthenticated(true);
      setRequires2FA(false);
      setTwoFactorCode("");
      toast.success("Logged in successfully!");
      
      if (officeLoaded && isOutlook) {
        getEmailContext();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid verification code");
    } finally {
      setLoginLoading(false);
    }
  };

  // Resend 2FA code
  const handleResend2FA = async () => {
    try {
      await axios.post(`${API_URL}/auth/resend-2fa`, { email, password });
      toast.success("New verification code sent");
    } catch (error) {
      toast.error("Failed to resend code");
    }
  };

  // Logout
  const handleLogout = () => {
    localStorage.removeItem("outlook_sales_token");
    setAuthToken(null);
    setIsAuthenticated(false);
    setCustomerData(null);
    toast.success("Logged out");
  };

  // Lookup customer by email
  const lookupCustomer = async (emailAddress) => {
    if (!emailAddress) return;
    
    setCustomerLoading(true);
    try {
      // Try to find contact first
      const contactRes = await axios.get(`${API_URL}/crm/contacts/search`, {
        params: { q: emailAddress },
        headers: { Authorization: `Bearer ${authToken}` }
      });

      if (contactRes.data.contacts?.length > 0) {
        const contact = contactRes.data.contacts[0];
        
        // If contact has company, get company details
        let company = null;
        if (contact.company_id) {
          try {
            const companyRes = await axios.get(`${API_URL}/crm/companies/${contact.company_id}`, {
              headers: { Authorization: `Bearer ${authToken}` }
            });
            company = companyRes.data;
          } catch (e) {
            console.error("Failed to fetch company:", e);
          }
        }

        setCustomerData({
          contact,
          company,
          found: true
        });

        // Load quotes and offers for this customer
        if (company?.id) {
          loadCustomerQuotes(company.id);
          loadCustomerOffers(company.id);
        }
      } else {
        setCustomerData({ found: false, searchedEmail: emailAddress });
      }
    } catch (error) {
      console.error("Customer lookup failed:", error);
      setCustomerData({ found: false, searchedEmail: emailAddress });
    } finally {
      setCustomerLoading(false);
    }
  };

  // Load quotes for customer
  const loadCustomerQuotes = async (companyId) => {
    setQuotesLoading(true);
    try {
      const response = await axios.get(`${API_URL}/offers`, {
        params: { company_id: companyId, type: "quote", limit: 10 },
        headers: { Authorization: `Bearer ${authToken}` }
      });
      setQuotes(response.data.offers || []);
    } catch (error) {
      console.error("Failed to load quotes:", error);
    } finally {
      setQuotesLoading(false);
    }
  };

  // Load offers for customer
  const loadCustomerOffers = async (companyId) => {
    setOffersLoading(true);
    try {
      const response = await axios.get(`${API_URL}/offers`, {
        params: { company_id: companyId, limit: 10 },
        headers: { Authorization: `Bearer ${authToken}` }
      });
      setOffers(response.data.offers || []);
    } catch (error) {
      console.error("Failed to load offers:", error);
    } finally {
      setOffersLoading(false);
    }
  };

  // Load pipeline stats
  const loadPipelineStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/outlook/get-sales-stats`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      setPipelineStats(response.data);
    } catch (error) {
      console.error("Failed to load pipeline stats:", error);
    }
  };

  // Create new quote — submits to the same endpoint as the public form
  const handleCreateQuote = async () => {
    try {
      const payload = {
        service_type: newQuote.service_type,
        origin_country: newQuote.origin_country, origin_city: newQuote.origin_city,
        origin_address: newQuote.origin_address || null,
        origin_port_airport: newQuote.origin_port_airport || null,
        destination_country: newQuote.destination_country, destination_city: newQuote.destination_city,
        destination_address: newQuote.destination_address || null,
        destination_port_airport: newQuote.destination_port_airport || null,
        container_type: newQuote.container_type || null,
        container_quantity: parseInt(newQuote.container_quantity || 1, 10),
        total_weight_kg: newQuote.total_weight_kg ? parseFloat(newQuote.total_weight_kg) : null,
        total_volume_cbm: newQuote.total_volume_cbm ? parseFloat(newQuote.total_volume_cbm) : null,
        commodity: newQuote.commodity || null,
        cargo_ready_date: newQuote.cargo_ready_date || null,
        incoterm: newQuote.incoterm || null,
        company_name: newQuote.company_name || customerData?.company?.name || "",
        contact_name: newQuote.contact_name || customerData?.contact?.full_name || "",
        email: newQuote.email || customerData?.contact?.email || "",
        phone: newQuote.phone || null,
        special_requirements: newQuote.special_requirements || null,
        document_ids: [],
      };
      const response = await axios.post(`${API_URL}/quotes/request`, payload, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      toast.success(`Quote draft created: ${response.data.quote_number}`);
      setShowNewQuoteDialog(false);
      setNewQuote(emptyQuote);
      setParseConfidence(null);
      if (customerData?.company?.id) loadCustomerQuotes(customerData.company.id);
    } catch (error) {
      const detail = error.response?.data?.detail;
      let msg = "Failed to create quote";
      if (Array.isArray(detail) && detail.length) {
        const f = detail[0];
        msg = `${(f.loc || []).slice(1).join(".") || "field"}: ${f.msg || "validation error"}`;
      } else if (typeof detail === "string") {
        msg = detail;
      }
      toast.error(msg);
    }
  };

  // Auto-parse the open Outlook email into a prefilled quote draft
  const prefillFromCurrentEmail = useCallback(async () => {
    if (!currentEmail) {
      toast.info("No email loaded — open an email in Outlook first");
      return;
    }
    setParsingEmail(true);
    setParseConfidence(null);
    try {
      const { data } = await axios.post(`${API_URL}/sales/parse-quote-from-email`, {
        subject: currentEmail.subject,
        body: currentEmail.body || currentEmail.body_preview || "",
        from_email: currentEmail.sender_email || currentEmail.from,
        from_name: currentEmail.sender_name || currentEmail.from_name,
        received_at: currentEmail.received_at,
      }, { headers: { Authorization: `Bearer ${authToken}` } });
      setNewQuote(prev => ({
        ...prev,
        service_type: data.service_type || "fcl",
        origin_country: data.origin_country || "",
        origin_city: data.origin_city || "",
        origin_address: data.origin_address || "",
        origin_port_airport: data.origin_port_airport || "",
        destination_country: data.destination_country || "",
        destination_city: data.destination_city || "",
        destination_address: data.destination_address || "",
        destination_port_airport: data.destination_port_airport || "",
        container_type: data.container_type || "",
        container_quantity: data.container_quantity || 1,
        total_weight_kg: data.total_weight_kg || "",
        total_volume_cbm: data.total_volume_cbm || "",
        commodity: data.commodity || "",
        cargo_ready_date: data.cargo_ready_date || "",
        incoterm: data.incoterm || "",
        special_requirements: data.special_requirements || "",
        company_name: data.company_name || prev.company_name,
        contact_name: data.contact_name || prev.contact_name,
        email: data.email || prev.email,
        phone: data.phone || prev.phone,
        temperature_controlled: !!data.temperature_controlled,
        temperature_celsius: data.temperature_celsius ?? "",
        hazardous: !!data.hazardous,
      }));
      setParseConfidence(data.confidence || null);
      setParseSource(data._source);
      toast.success(`Prefilled from email (confidence ${Math.round((data.confidence || 0) * 100)}%)`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not parse email");
    } finally {
      setParsingEmail(false);
    }
  }, [currentEmail, authToken]);

  // When the new-quote dialog opens AND we have a current email, auto-parse it
  useEffect(() => {
    if (showNewQuoteDialog && currentEmail && !newQuote.commodity) {
      prefillFromCurrentEmail();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showNewQuoteDialog, currentEmail]);

  // Login screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-green-50 to-white p-4">
        <Card className="max-w-sm mx-auto mt-8">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 h-16 w-16 rounded-full bg-green-600 flex items-center justify-center">
              <TrendingUp className="h-8 w-8 text-white" />
            </div>
            <CardTitle>myEuropacific Sales</CardTitle>
            <CardDescription>
              {requires2FA 
                ? `Enter the code sent to ${twoFactorEmail}`
                : "Sign in to access sales tools"
              }
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {requires2FA ? (
              // 2FA Verification Form
              <>
                <div>
                  <Label>Verification Code</Label>
                  <Input
                    type="text"
                    value={twoFactorCode}
                    onChange={(e) => setTwoFactorCode(e.target.value)}
                    placeholder="Enter 6-digit code"
                    maxLength={6}
                    autoFocus
                    onKeyPress={(e) => e.key === "Enter" && handleVerify2FA()}
                  />
                </div>
                <Button 
                  onClick={handleVerify2FA} 
                  disabled={loginLoading}
                  className="w-full bg-green-600 hover:bg-green-700"
                >
                  {loginLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Verify & Sign In
                </Button>
                <div className="flex justify-between text-sm">
                  <button 
                    type="button"
                    onClick={handleResend2FA}
                    className="text-green-600 hover:underline"
                  >
                    Resend code
                  </button>
                  <button 
                    type="button"
                    onClick={() => {
                      setRequires2FA(false);
                      setTwoFactorCode("");
                    }}
                    className="text-gray-500 hover:underline"
                  >
                    Back to login
                  </button>
                </div>
              </>
            ) : (
              // Regular Login Form
              <>
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                  />
                </div>
                <div>
                  <Label>Password</Label>
                  <Input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    onKeyPress={(e) => e.key === "Enter" && handleLogin()}
                  />
                </div>
                <Button 
                  onClick={handleLogin} 
                  disabled={loginLoading}
                  className="w-full bg-green-600 hover:bg-green-700"
                >
                  {loginLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Sign In
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-green-600 text-white px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            <span className="font-semibold">Sales Panel</span>
          </div>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={handleLogout}
            className="text-white hover:bg-green-700"
          >
            Sign Out
          </Button>
        </div>
        {currentEmail && (
          <div className="mt-2 text-xs text-green-100 truncate">
            From: {currentEmail.from}
          </div>
        )}
      </div>

      {/* Main Content */}
      <div className="p-4">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid grid-cols-4 mb-4">
            <TabsTrigger value="customer" className="text-xs">
              <Users className="h-3 w-3 mr-1" />
              Customer
            </TabsTrigger>
            <TabsTrigger value="quotes" className="text-xs">
              <Receipt className="h-3 w-3 mr-1" />
              Quotes
            </TabsTrigger>
            <TabsTrigger value="offers" className="text-xs">
              <Briefcase className="h-3 w-3 mr-1" />
              Offers
            </TabsTrigger>
            <TabsTrigger value="pipeline" className="text-xs">
              <BarChart3 className="h-3 w-3 mr-1" />
              Pipeline
            </TabsTrigger>
          </TabsList>

          {/* Customer Tab */}
          <TabsContent value="customer">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Building2 className="h-4 w-4" />
                  Customer Info
                </CardTitle>
              </CardHeader>
              <CardContent>
                {customerLoading ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-green-600" />
                  </div>
                ) : customerData?.found ? (
                  <div className="space-y-4">
                    {/* Contact Info */}
                    <div className="bg-slate-50 rounded-lg p-3">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center">
                          <Users className="h-5 w-5 text-green-600" />
                        </div>
                        <div>
                          <p className="font-medium">{customerData.contact?.name}</p>
                          <p className="text-xs text-slate-500">{customerData.contact?.email}</p>
                        </div>
                      </div>
                      {customerData.contact?.position && (
                        <p className="text-sm text-slate-600 mt-2">{customerData.contact.position}</p>
                      )}
                      {customerData.contact?.phone && (
                        <p className="text-sm text-slate-500 flex items-center gap-1 mt-1">
                          <Phone className="h-3 w-3" />
                          {customerData.contact.phone}
                        </p>
                      )}
                    </div>

                    {/* Company Info */}
                    {customerData.company && (
                      <div className="border rounded-lg p-3">
                        <div className="flex items-center gap-2 mb-2">
                          <Building2 className="h-4 w-4 text-slate-500" />
                          <span className="font-medium">{customerData.company.name}</span>
                        </div>
                        {customerData.company.country && (
                          <p className="text-sm text-slate-500">{customerData.company.city}, {customerData.company.country}</p>
                        )}
                        {customerData.company.industry && (
                          <Badge variant="outline" className="mt-2">{customerData.company.industry}</Badge>
                        )}
                        {customerData.company.tags?.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {customerData.company.tags.map((tag, i) => (
                              <Badge key={i} variant="secondary" className="text-xs">{tag}</Badge>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Quick Actions */}
                    <div className="flex gap-2">
                      <Button 
                        size="sm" 
                        className="flex-1 bg-green-600 hover:bg-green-700"
                        onClick={() => setShowNewQuoteDialog(true)}
                      >
                        <Plus className="h-3 w-3 mr-1" />
                        New Quote
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        className="flex-1"
                        onClick={() => window.open(`${API_BASE_URL}/crm`, '_blank')}
                      >
                        <ExternalLink className="h-3 w-3 mr-1" />
                        Full Profile
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Users className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-sm text-slate-500">
                      {customerData?.searchedEmail 
                        ? `No customer found for ${customerData.searchedEmail}`
                        : "Open an email to lookup customer"
                      }
                    </p>
                    <Button 
                      size="sm" 
                      variant="outline" 
                      className="mt-4"
                      onClick={() => window.open(`${API_BASE_URL}/crm`, '_blank')}
                    >
                      <Plus className="h-3 w-3 mr-1" />
                      Create Contact
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Quotes Tab */}
          <TabsContent value="quotes">
            <Card>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Receipt className="h-4 w-4" />
                    Recent Quotes
                  </CardTitle>
                  <Button 
                    size="sm" 
                    onClick={() => setShowNewQuoteDialog(true)}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    New
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {quotesLoading ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin" />
                  </div>
                ) : quotes.length > 0 ? (
                  <ScrollArea className="h-[300px]">
                    <div className="space-y-2">
                      {quotes.map((quote) => (
                        <div 
                          key={quote.id}
                          className="border rounded-lg p-3 hover:bg-slate-50 cursor-pointer"
                        >
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-medium text-sm">{quote.reference || quote.id?.slice(0, 8)}</span>
                            <Badge variant={quote.status === 'accepted' ? 'success' : 'secondary'}>
                              {quote.status}
                            </Badge>
                          </div>
                          <p className="text-xs text-slate-500">
                            {quote.origin} → {quote.destination}
                          </p>
                          {quote.amount && (
                            <p className="text-sm font-semibold text-green-600 mt-1">
                              €{quote.amount.toLocaleString()}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                ) : (
                  <div className="text-center py-8">
                    <Receipt className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-sm text-slate-500">No quotes yet</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Offers Tab */}
          <TabsContent value="offers">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Briefcase className="h-4 w-4" />
                  Active Offers
                </CardTitle>
              </CardHeader>
              <CardContent>
                {offersLoading ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin" />
                  </div>
                ) : offers.length > 0 ? (
                  <ScrollArea className="h-[300px]">
                    <div className="space-y-2">
                      {offers.map((offer) => (
                        <div 
                          key={offer.id}
                          className="border rounded-lg p-3 hover:bg-slate-50 cursor-pointer"
                        >
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-medium text-sm">{offer.reference}</span>
                            <Badge variant={offer.status === 'won' ? 'success' : offer.status === 'lost' ? 'destructive' : 'secondary'}>
                              {offer.status}
                            </Badge>
                          </div>
                          <p className="text-xs text-slate-500">{offer.description}</p>
                          {offer.value && (
                            <p className="text-sm font-semibold text-green-600 mt-1">
                              €{offer.value.toLocaleString()}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                ) : (
                  <div className="text-center py-8">
                    <Briefcase className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-sm text-slate-500">No active offers</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Pipeline Tab */}
          <TabsContent value="pipeline">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <BarChart3 className="h-4 w-4" />
                  Sales Pipeline
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-blue-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-blue-600">
                      {pipelineStats?.openQuotes || 0}
                    </p>
                    <p className="text-xs text-slate-500">Open Quotes</p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-green-600">
                      {pipelineStats?.wonThisMonth || 0}
                    </p>
                    <p className="text-xs text-slate-500">Won This Month</p>
                  </div>
                  <div className="bg-yellow-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-yellow-600">
                      {pipelineStats?.pendingOffers || 0}
                    </p>
                    <p className="text-xs text-slate-500">Pending Offers</p>
                  </div>
                  <div className="bg-purple-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-purple-600">
                      €{(pipelineStats?.pipelineValue || 0).toLocaleString()}
                    </p>
                    <p className="text-xs text-slate-500">Pipeline Value</p>
                  </div>
                </div>
                <Button 
                  variant="outline" 
                  className="w-full mt-4"
                  onClick={() => window.open(`${API_BASE_URL}/offers`, '_blank')}
                >
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Open Full Dashboard
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* New Quote Dialog — mirrors europacific.com/en/quote, prefilled from email */}
      <Dialog open={showNewQuoteDialog} onOpenChange={setShowNewQuoteDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="new-quote-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-green-600" />
              New Quote Request
            </DialogTitle>
            <p className="text-xs text-slate-500">
              Mirrors europacific.com/en/quote. Fields prefill from the open email — review &amp; submit.
            </p>
          </DialogHeader>

          {/* Prefill banner */}
          <div className="flex items-center justify-between gap-2 bg-emerald-50 border border-emerald-200 rounded-lg p-2" data-testid="parse-banner">
            <div className="text-xs text-emerald-700">
              {parsingEmail ? (
                <span className="flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" /> Parsing email…</span>
              ) : parseConfidence != null ? (
                <span>
                  ✨ Prefilled from <span className="font-medium">{currentEmail?.subject?.slice(0, 50) || "email"}</span> · confidence {Math.round(parseConfidence * 100)}%
                  {parseSource === "heuristic_fallback" && <span className="ml-1 text-amber-700">(regex fallback)</span>}
                </span>
              ) : (
                <span>Click "Re-parse email" to auto-fill from the open Outlook email.</span>
              )}
            </div>
            <Button
              size="sm" variant="outline"
              onClick={prefillFromCurrentEmail}
              disabled={parsingEmail || !currentEmail}
              className="text-xs h-7"
              data-testid="reparse-email-btn"
            >
              <RefreshCw className={`h-3 w-3 mr-1 ${parsingEmail ? "animate-spin" : ""}`} />
              Re-parse
            </Button>
          </div>

          <div className="space-y-4 mt-2">
            {/* === Service === */}
            <div>
              <Label className="text-xs uppercase text-slate-500">Service</Label>
              <div className="grid grid-cols-5 gap-1 mt-1">
                {["fcl", "lcl", "air", "inland", "multimodal"].map(s => (
                  <button
                    key={s} type="button"
                    onClick={() => setNewQuote(p => ({ ...p, service_type: s }))}
                    className={`px-2 py-2 rounded-md text-[11px] font-medium border-2 transition-all ${
                      newQuote.service_type === s
                        ? "bg-emerald-50 border-emerald-500 text-emerald-700"
                        : "bg-white border-slate-200 text-slate-600 hover:border-slate-300"
                    }`}
                    data-testid={`service-${s}`}
                  >{s.toUpperCase()}</button>
                ))}
              </div>
            </div>

            {/* === Route === */}
            <div className="border-l-4 border-emerald-200 pl-3">
              <p className="text-xs uppercase text-slate-500 font-semibold mb-2">Route</p>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-[11px]">Origin City</Label>
                  <Input value={newQuote.origin_city}
                    onChange={(e) => setNewQuote(p => ({ ...p, origin_city: e.target.value }))}
                    placeholder="Kosice" className="h-8 text-sm" data-testid="origin-city" />
                </div>
                <div>
                  <Label className="text-[11px]">Origin Country</Label>
                  <Input value={newQuote.origin_country}
                    onChange={(e) => setNewQuote(p => ({ ...p, origin_country: e.target.value }))}
                    placeholder="Slovakia" className="h-8 text-sm" data-testid="origin-country" />
                </div>
                <div>
                  <Label className="text-[11px]">Origin Port / Airport</Label>
                  <Input value={newQuote.origin_port_airport}
                    onChange={(e) => setNewQuote(p => ({ ...p, origin_port_airport: e.target.value }))}
                    placeholder="Optional" className="h-8 text-sm" />
                </div>
                <div>
                  <Label className="text-[11px]">Origin Address</Label>
                  <Input value={newQuote.origin_address}
                    onChange={(e) => setNewQuote(p => ({ ...p, origin_address: e.target.value }))}
                    placeholder="Optional pickup address" className="h-8 text-sm" />
                </div>
                <div>
                  <Label className="text-[11px]">Destination City</Label>
                  <Input value={newQuote.destination_city}
                    onChange={(e) => setNewQuote(p => ({ ...p, destination_city: e.target.value }))}
                    placeholder="Tema" className="h-8 text-sm" data-testid="destination-city" />
                </div>
                <div>
                  <Label className="text-[11px]">Destination Country</Label>
                  <Input value={newQuote.destination_country}
                    onChange={(e) => setNewQuote(p => ({ ...p, destination_country: e.target.value }))}
                    placeholder="Ghana" className="h-8 text-sm" />
                </div>
                <div>
                  <Label className="text-[11px]">Destination Port / Airport</Label>
                  <Input value={newQuote.destination_port_airport}
                    onChange={(e) => setNewQuote(p => ({ ...p, destination_port_airport: e.target.value }))}
                    placeholder="TEMA / KOPER" className="h-8 text-sm" />
                </div>
                <div>
                  <Label className="text-[11px]">Incoterm</Label>
                  <select
                    value={newQuote.incoterm}
                    onChange={(e) => setNewQuote(p => ({ ...p, incoterm: e.target.value }))}
                    className="h-8 text-sm w-full border rounded-md px-2"
                    data-testid="incoterm"
                  >
                    <option value="">—</option>
                    {["EXW","FCA","FAS","FOB","CFR","CIF","CPT","CIP","DAP","DPU","DDP"].map(i => (
                      <option key={i} value={i}>{i}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* === Cargo === */}
            <div className="border-l-4 border-blue-200 pl-3">
              <p className="text-xs uppercase text-slate-500 font-semibold mb-2">Cargo</p>
              <div className="grid grid-cols-3 gap-2">
                {newQuote.service_type === "fcl" && (
                  <>
                    <div>
                      <Label className="text-[11px]">Container Type</Label>
                      <select
                        value={newQuote.container_type}
                        onChange={(e) => setNewQuote(p => ({ ...p, container_type: e.target.value }))}
                        className="h-8 text-sm w-full border rounded-md px-2"
                        data-testid="container-type"
                      >
                        <option value="">—</option>
                        {["20GP","40GP","40HC","45HC","20RF","40RF"].map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                    <div>
                      <Label className="text-[11px]">Qty</Label>
                      <Input type="number" min="1" value={newQuote.container_quantity}
                        onChange={(e) => setNewQuote(p => ({ ...p, container_quantity: e.target.value }))}
                        className="h-8 text-sm" data-testid="container-qty" />
                    </div>
                  </>
                )}
                <div>
                  <Label className="text-[11px]">Weight (kg)</Label>
                  <Input type="number" value={newQuote.total_weight_kg}
                    onChange={(e) => setNewQuote(p => ({ ...p, total_weight_kg: e.target.value }))}
                    placeholder="26500" className="h-8 text-sm" data-testid="weight" />
                </div>
                <div>
                  <Label className="text-[11px]">Volume (cbm)</Label>
                  <Input type="number" step="0.1" value={newQuote.total_volume_cbm}
                    onChange={(e) => setNewQuote(p => ({ ...p, total_volume_cbm: e.target.value }))}
                    placeholder="optional" className="h-8 text-sm" />
                </div>
                <div className="col-span-2">
                  <Label className="text-[11px]">Commodity</Label>
                  <Input value={newQuote.commodity}
                    onChange={(e) => setNewQuote(p => ({ ...p, commodity: e.target.value }))}
                    placeholder="frozen meat" className="h-8 text-sm" data-testid="commodity" />
                </div>
                <div>
                  <Label className="text-[11px]">Cargo Ready</Label>
                  <Input type="date" value={newQuote.cargo_ready_date}
                    onChange={(e) => setNewQuote(p => ({ ...p, cargo_ready_date: e.target.value }))}
                    className="h-8 text-sm" />
                </div>
              </div>
              <div className="flex gap-4 mt-2">
                <label className="flex items-center gap-1 text-xs text-slate-600">
                  <input type="checkbox" checked={newQuote.temperature_controlled}
                    onChange={(e) => setNewQuote(p => ({ ...p, temperature_controlled: e.target.checked }))} />
                  Temperature-controlled
                </label>
                {newQuote.temperature_controlled && (
                  <Input type="number" value={newQuote.temperature_celsius}
                    onChange={(e) => setNewQuote(p => ({ ...p, temperature_celsius: e.target.value }))}
                    placeholder="-18" className="h-7 text-xs w-20" />
                )}
                <label className="flex items-center gap-1 text-xs text-slate-600">
                  <input type="checkbox" checked={newQuote.hazardous}
                    onChange={(e) => setNewQuote(p => ({ ...p, hazardous: e.target.checked }))} />
                  Hazardous (DG)
                </label>
              </div>
              <div className="mt-2">
                <Label className="text-[11px]">Special Requirements</Label>
                <Textarea value={newQuote.special_requirements}
                  onChange={(e) => setNewQuote(p => ({ ...p, special_requirements: e.target.value }))}
                  placeholder="Multi-port discharge, reefer specs, dispatch notes..."
                  rows={3} className="text-xs"
                  data-testid="special-requirements" />
              </div>
            </div>

            {/* === Contact === */}
            <div className="border-l-4 border-violet-200 pl-3">
              <p className="text-xs uppercase text-slate-500 font-semibold mb-2">Contact</p>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-[11px]">Company Name</Label>
                  <Input value={newQuote.company_name}
                    onChange={(e) => setNewQuote(p => ({ ...p, company_name: e.target.value }))}
                    placeholder="CF&S Lithuania" className="h-8 text-sm"
                    data-testid="company-name" />
                </div>
                <div>
                  <Label className="text-[11px]">Contact Name</Label>
                  <Input value={newQuote.contact_name}
                    onChange={(e) => setNewQuote(p => ({ ...p, contact_name: e.target.value }))}
                    className="h-8 text-sm" data-testid="contact-name" />
                </div>
                <div>
                  <Label className="text-[11px]">Email</Label>
                  <Input value={newQuote.email}
                    onChange={(e) => setNewQuote(p => ({ ...p, email: e.target.value }))}
                    className="h-8 text-sm" data-testid="contact-email" />
                </div>
                <div>
                  <Label className="text-[11px]">Phone</Label>
                  <Input value={newQuote.phone}
                    onChange={(e) => setNewQuote(p => ({ ...p, phone: e.target.value }))}
                    placeholder="optional" className="h-8 text-sm" />
                </div>
              </div>
            </div>
          </div>

          <DialogFooter className="mt-3">
            <Button variant="outline" onClick={() => setShowNewQuoteDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateQuote} className="bg-emerald-600 hover:bg-emerald-700" data-testid="create-quote-submit">
              Create Quote Draft
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Suggestion Box - Fixed at bottom */}
      <div className="sticky bottom-0 left-0 right-0 mx-3 mb-2 p-2 bg-green-50 border border-green-200 rounded-lg shadow-lg">
        <p className="text-[10px] font-medium text-green-700 mb-1 flex items-center gap-1">
          <Lightbulb className="h-3 w-3" /> Have a suggestion?
        </p>
        <div className="flex gap-1">
          <Input
            value={suggestionText}
            onChange={(e) => setSuggestionText(e.target.value)}
            placeholder="Share your idea..."
            className="text-xs flex-1 bg-white h-7"
            onKeyDown={(e) => e.key === "Enter" && submitSuggestion()}
          />
          <Button 
            size="sm" 
            onClick={submitSuggestion}
            disabled={submittingSuggestion || !suggestionText.trim()}
            className="bg-green-600 hover:bg-green-700 h-7 px-2"
          >
            {submittingSuggestion ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
          </Button>
        </div>
      </div>
    </div>
  );
}
